import "./address-search-box.css";
import { Data } from "../../../../../ts/models/data";
import { Context } from "../../../../../ts/models/context";
import { GeoSearchProviderFactory } from "../../../../../ts/services/geo-search/geo-search-provider-factory";
import { IGeoSearchProvider } from "../../../../../ts/services/geo-search/geo-search-provider.interface";
import { AutocompleteProvider } from "../../../../../ts/types/enums";
import { IMapInstance } from "../../../../services/maps/map-provider.interface";
import { LocalizationService } from "../../../../services/localization-service";

export class AddressSearchBox {
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _containerElement: HTMLElement;
    private readonly _localizationService: LocalizationService;

    private readonly _geoSearchProvider: IGeoSearchProvider | null = null;

    constructor(
        containerElement: HTMLElement,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
    ) {
        this._containerElement = containerElement;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;

        const providerConfigurations = this._getProviderConfigurations();
        this._geoSearchProvider = GeoSearchProviderFactory.getGeoSearchProvider(
            providerConfigurations.providerId,
            providerConfigurations.apiKey,
        );
    }

    private _getProviderConfigurations(): { providerId: AutocompleteProvider; apiKey: string } {
        const providerId = this._data.getBrandProperty("Settings").OOS.autocompleteProvider;
        let apiKey = "";

        if (providerId == AutocompleteProvider.GOOGLE) apiKey = this._context.getProperty("googleMapApiKey");
        else if (providerId == AutocompleteProvider.HERE) apiKey = this._context.getProperty("hereMapApiKey");

        return {
            providerId: providerId,
            apiKey: apiKey,
        };
    }

    public async bindAutoCompleteAsync(
        mapInstance: IMapInstance,
        onPlaceSelected: (coords: { lat: number; lng: number }) => void,
    ): Promise<void> {
        const container = document.getElementById("div_store_selector_autocomplete_container");

        if (container) {
            await this._geoSearchProvider?.bindAutocompleteAsync(
                container,
                mapInstance,
                ({ lat, lng }) => {
                    onPlaceSelected({ lat, lng });
                },
                true
            );

            const placeholderText = this._localizationService.names("SearchCityStateOrZipCode");
            container?.querySelector("gmp-place-autocomplete")?.setAttribute("placeholder", placeholderText);
        }
    }

    public async getCoordsFromZipCodeAsync(
        zipCode: string,
        countryId: string,
    ): Promise<{ lat: number; lng: number } | null> {
        const countryName =
            this._data.getBrandProperty("Countries").find((c) => c.CountryID === Number(countryId))?.Name ??
            "United States of America";

        const addressQuery =
            zipCode && zipCode.trim().length > 0 ? `${zipCode}, ${countryName}` : countryName;

        const coords = await this._geoSearchProvider?.geocodeAsync?.(addressQuery);
        return coords ?? null;
    }
}
